import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAppConfig } from '../../contexts/AppConfigContext';
import { useAdmin } from '../../contexts/AdminContext';
import { useOnboardingStatus } from '../../hooks/useOnboardingStatus';
import { useTwoFactorStatus } from '../../hooks/useTwoFactorStatus';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';
import { usePermissions } from '../../hooks/usePermissions';
import AppLoadingScreen from '../shared/AppLoadingScreen';

interface AppInitializerProps {
  children: React.ReactNode;
}

/**
 * Orchestrates all app initialization checks and shows a single loading screen
 * until everything is ready. Replaces the sequential spinner-per-guard pattern.
 *
 * Sits inside providers (AuthProvider, AppConfigProvider, AdminProvider) but
 * above the route tree. Public/auth routes are NOT wrapped by this component.
 */
const AppInitializer: React.FC<AppInitializerProps> = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isLoading: configLoading } = useAppConfig();
  const { isLoading: adminLoading } = useAdmin();
  const { isChecking: onboardingChecking, needsOnboarding } = useOnboardingStatus();
  const { isChecking: twoFactorChecking, needsSetup: needs2FA } = useTwoFactorStatus();
  const { isChecking: subscriptionChecking, needsSubscription, subscriptionInactive } = useSubscriptionStatus();
  const { canViewBilling } = usePermissions();

  // Don't block if user isn't authenticated — let routes handle login redirect
  const isUnauthenticated = !authLoading && !isAuthenticated;
  const contextReady = !authLoading && !configLoading && !adminLoading;
  const guardsReady = !onboardingChecking && !twoFactorChecking && !subscriptionChecking;
  const isReady = isUnauthenticated || (contextReady && guardsReady);

  // Fade-out: once ready, delay content render briefly for smooth transition
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (isReady) {
      // Minimal delay so the loading screen fades out rather than cuts
      const timer = setTimeout(() => setShowContent(true), 30);
      return () => clearTimeout(timer);
    }
  }, [isReady]);

  if (!showContent) {
    return (
      <div
        className={`transition-opacity duration-200 ${isReady ? 'opacity-0' : 'opacity-100'}`}
      >
        <AppLoadingScreen />
      </div>
    );
  }

  // Handle guard redirects — skip if already on the target page
  if (needsOnboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  if (needs2FA && !needsOnboarding && location.pathname !== '/setup-2fa') {
    return <Navigate to="/setup-2fa" replace />;
  }
  // Only billing-eligible roles (Super Users) get redirected to /billing when
  // the tenant needs a subscription. Team Members and Scheduling Assistants
  // are not responsible for billing and should never see plan/billing UI —
  // they continue into the app and let the responsible admin handle it.
  if (canViewBilling && needsSubscription && !needsOnboarding && !needs2FA && location.pathname !== '/billing') {
    return <Navigate to="/billing" replace />;
  }
  if (canViewBilling && subscriptionInactive && !needsOnboarding && !needs2FA && location.pathname !== '/billing') {
    return <Navigate to="/billing" replace />;
  }

  return <>{children}</>;
};

export default AppInitializer;
